'use client';

import type { CSSProperties, ReactNode } from 'react';

import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '@kit/ui/drawer';
import { cn } from '@kit/ui/utils';

import { useNativePresentation } from './native/_lib/use-native-presentation';

/**
 * Shared bottom-anchored "view details" drawer used by every customer-facing
 * surface that needs to show long-form copy (packages, classes, membership
 * plans). Powered by vaul via `@kit/ui/drawer` — same iOS-feel rubber band as
 * the venue switcher (see top-bar-location-switcher.tsx).
 *
 * Callers pre-localize `description` and `features` (e.g. via
 * selectPackageCopy / selectMembershipPlanCopy); this component is
 * presentational only.
 *
 * Optional `image_url` renders a 16:9 hero at the top, matching the purchase
 * cards. `color` paints a thin accent rail on the inline-start edge of the
 * image when set. `footer` is a slot for caller-specific blocks (e.g.
 * class-card uses it for eligibility warnings).
 */
export function DetailsDrawer({
  open,
  onOpenChange,
  name,
  subtitle,
  description,
  features,
  imageUrl,
  imagePosition,
  color,
  footer,
  contentClassName,
  contentStyle,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  name: string;
  subtitle?: string;
  description?: string | null;
  features?: string[];
  imageUrl?: string | null;
  imagePosition?: string;
  color?: string | null;
  footer?: ReactNode;
  contentClassName?: string;
  contentStyle?: CSSProperties;
}) {
  const { t } = useTranslation('bookings');
  const presentation = useNativePresentation();
  const hasFeatures = features !== undefined && features.length > 0;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        blockOutside={!!presentation.className}
        overlayClassName="bg-black/45 backdrop-blur-[2px] dark:bg-black/60"
        className={cn(
          'md:!right-[calc(50%-21rem)] md:!left-[calc(50%-21rem)]',
          imageUrl && 'overflow-hidden [&>div:first-child]:hidden',
          presentation.className,
          contentClassName,
        )}
        style={{ ...presentation.style, ...contentStyle }}
      >
        <div className="relative flex max-h-[85vh] min-h-0 flex-col">
          {imageUrl && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute top-2 left-1/2 z-10 h-1 w-10 -translate-x-1/2 rounded-full bg-white/60 shadow-sm"
            />
          )}
          {/* Floating close button — top-end corner, layered above the hero
              image so it never gets pushed below the fold on tall cards. */}
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute end-3 top-3 z-10 size-11 shrink-0 rounded-full bg-black/40 p-0 text-white backdrop-blur-sm hover:bg-black/60"
              style={{
                width: 44,
                height: 44,
                minHeight: 44,
                borderRadius: '50%',
              }}
              aria-label={t('close', { defaultValue: 'Close' })}
            >
              <X className="h-4 w-4" />
            </Button>
          </DrawerClose>

          {imageUrl ? (
            <div className="relative w-full shrink-0 overflow-hidden">
              <div className="relative aspect-[16/9] w-full">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={imageUrl}
                  alt=""
                  className="h-full w-full"
                  style={{ objectFit: 'cover', objectPosition: imagePosition }}
                  draggable={false}
                />
                {color ? (
                  <div
                    aria-hidden
                    className="absolute inset-y-0 start-0 w-1"
                    style={{ backgroundColor: color }}
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="px-6 pe-14 pt-4 pb-2">
            {subtitle ? (
              <div className="text-muted-foreground mb-1 text-xs tracking-wider uppercase">
                {subtitle}
              </div>
            ) : null}
            <DrawerTitle className="text-xl font-semibold" dir="auto">
              {name}
            </DrawerTitle>
            <DrawerDescription className="sr-only">
              {t('packageDetailsDescription', {
                defaultValue: 'Detailed information',
              })}
            </DrawerDescription>
          </div>

          <div className="overflow-y-auto px-6 pb-16">
            {description ? (
              <p
                className="text-muted-foreground text-sm whitespace-pre-line"
                dir="auto"
              >
                {description}
              </p>
            ) : null}

            {hasFeatures ? (
              <ul className={description ? 'mt-6 space-y-2' : 'space-y-2'}>
                {features!.map((feature, i) => (
                  <li
                    key={i}
                    dir="auto"
                    className="flex items-start gap-2 text-sm"
                  >
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            ) : null}

            {footer ? <div className="mt-6">{footer}</div> : null}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
